import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Input, Modal, Radio, Select, Tag, Alert, Space, Typography } from "antd";
import { UserOutlined, TeamOutlined, BankOutlined } from "@ant-design/icons";

const { Text } = Typography;
const PRIMARY = "#242166";

/**
 * Choix du destinataire du chèque pour un règlement victime.
 * Le règlement reste rattaché à la VICTIME ; ce choix ne change que le nom à
 * l'ordre duquel le chèque est libellé (null = la victime elle-même).
 *
 * Candidats « représentant » = victime.representant + victime.ayantsDroit.
 *
 * onConfirm(representantName | null, isAyantDroit, beneficiaireMoralCodeId | null) —
 * representantName est une chaîne (nom complet) ou null si le chèque est à l'ordre de la victime ;
 * beneficiaireMoralCodeId non-null = le chèque est libellé à un établissement (ex. BNCB pour un
 * dossier transfrontalier carte brune).
 *
 * carteBrune + bncb : si le dossier est transfrontalier et que le BNCB est configuré, l'option
 * « Bureau National de la Carte Brune » est PROPOSÉE PAR DÉFAUT (modifiable).
 */
const ChoixBeneficiaireChequeModal = ({ open, onClose, victime, onConfirm, loading, title, okText, carteBrune, bncb, motifOptions = [] }) => {
    const bncbActif = !!(carteBrune && bncb?.codeId);
    const candidates = useMemo(() => {
        const list = [];
        const fullName = (p) => `${p?.nom || ""} ${p?.prenom || ""}`.trim();
        // Principal (par défaut = le plus récent) en premier → pré-sélectionné. Le rôle reflète la
        // CATÉGORIE saisie (Personne.qualite : ex. « Personne ayant pris en charge les frais ») si elle
        // est renseignée, sinon « Représentant (par défaut) ».
        if (victime?.representant && fullName(victime.representant)) {
            const q = (victime.representant.qualite || "").trim();
            list.push({ value: fullName(victime.representant), role: q || "Représentant (par défaut)", personne: victime.representant });
        }
        // Tous les représentants du vivier (plusieurs possibles).
        (victime?.representants || []).forEach((r) => {
            const n = fullName(r);
            if (n && !list.some((c) => c.value === n)) {
                const q = (r.qualite || "").trim();
                list.push({ value: n, role: q || "Représentant", personne: r });
            }
        });
        (victime?.ayantsDroit || []).forEach((a) => {
            const n = fullName(a);
            if (n && !list.some((c) => c.value === n)) {
                list.push({ value: n, role: "Ayant droit", personne: a });
            }
        });
        return list;
    }, [victime]);

    const isDecede = !!victime?.decesSinistre;
    const victimeName = `${victime?.personne?.nom || ""} ${victime?.personne?.prenom || ""}`.trim();

    const [mode, setMode] = useState("VICTIME"); // BNCB | VICTIME | REPRESENTANT
    const [selectedName, setSelectedName] = useState(null);
    const [motifText, setMotifText] = useState(""); // motif de règlement OBLIGATOIRE

    // À l'ouverture : carte brune → BNCB par défaut (modifiable) ; sinon décédée → représentant ; blessée → victime.
    useEffect(() => {
        if (!open) return;
        const defaultMode = bncbActif ? "BNCB" : (isDecede && candidates.length ? "REPRESENTANT" : "VICTIME");
        setMode(defaultMode);
        setSelectedName(candidates.length ? candidates[0].value : null);
        setMotifText("");
    }, [open, isDecede, candidates, bncbActif]);

    const confirmDisabled = (mode === "REPRESENTANT" && !selectedName) || !(motifText || "").trim();

    const handleOk = () => {
        if (confirmDisabled) return;
        const motif = (motifText || "").trim();
        if (mode === "BNCB") {
            onConfirm(null, false, bncb?.codeId, motif);
            return;
        }
        if (mode !== "REPRESENTANT") {
            onConfirm(null, false, null, motif);
            return;
        }
        const isAyantDroit = candidates.find((c) => c.value === selectedName)?.role === "Ayant droit";
        onConfirm(selectedName, isAyantDroit, null, motif);
    };

    const optionStyle = (active) => ({
        flex: 1,
        border: `1.5px solid ${active ? PRIMARY : "#e5e7eb"}`,
        background: active ? `${PRIMARY}0d` : "#fff",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        transition: "all .15s",
    });

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            confirmLoading={loading}
            okButtonProps={{ disabled: confirmDisabled, style: { background: PRIMARY } }}
            okText={okText || "Confirmer le règlement"}
            cancelText="Annuler"
            width={560}
            title={<span style={{ color: PRIMARY, fontWeight: 600 }}>{title || "À l'ordre de qui libeller le chèque ?"}</span>}
            zIndex={3000}
        >
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Bénéficiaire (victime) : </Text>
                <Tag color={isDecede ? "red" : "blue"} icon={<UserOutlined />}>
                    {victimeName || "—"} · {isDecede ? "Décédée" : "Blessée"}
                </Tag>
            </div>

            {bncbActif && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Dossier transfrontalier (carte brune CEDEAO)"
                    description={<>Le chèque est proposé au nom du <Text strong>Bureau National de la Carte Brune (BNCB)</Text>. Vous pouvez le libeller autrement ci-dessous.</>}
                />
            )}

            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} style={{ width: "100%" }}>
                <Space direction="horizontal" style={{ width: "100%" }} size={12} wrap>
                    {bncbActif && (
                        <label style={optionStyle(mode === "BNCB")}>
                            <Radio value="BNCB" />
                            <div style={{ marginTop: 8 }}>
                                <BankOutlined style={{ color: PRIMARY, marginRight: 6 }} />
                                <Text strong>BNCB (par défaut)</Text>
                                <div><Text type="secondary" style={{ fontSize: 12 }}>{bncb?.nom || "Bureau National de la Carte Brune"}</Text></div>
                            </div>
                        </label>
                    )}
                    <label style={optionStyle(mode === "VICTIME")}>
                        <Radio value="VICTIME" />
                        <div style={{ marginTop: 8 }}>
                            <UserOutlined style={{ color: PRIMARY, marginRight: 6 }} />
                            <Text strong>La victime</Text>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{victimeName}</Text></div>
                        </div>
                    </label>
                    <label style={{ ...optionStyle(mode === "REPRESENTANT"), opacity: candidates.length ? 1 : 0.55 }}>
                        <Radio value="REPRESENTANT" disabled={!candidates.length} />
                        <div style={{ marginTop: 8 }}>
                            <TeamOutlined style={{ color: PRIMARY, marginRight: 6 }} />
                            <Text strong>Un représentant</Text>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>Représentant / ayant droit</Text></div>
                        </div>
                    </label>
                </Space>
            </Radio.Group>

            {mode === "REPRESENTANT" && (
                <div style={{ marginTop: 16 }}>
                    {candidates.length ? (
                        <Select
                            style={{ width: "100%" }}
                            size="large"
                            value={selectedName}
                            onChange={setSelectedName}
                            placeholder="Choisir le représentant"
                            options={candidates.map((c) => ({
                                value: c.value,
                                label: (
                                    <span>
                                        {c.personne?.typePersonne === "MORALE" ? <BankOutlined style={{ marginRight: 6 }} /> : <UserOutlined style={{ marginRight: 6 }} />}
                                        {c.value} <Tag style={{ marginLeft: 6 }} color={c.role === "Représentant" ? "geekblue" : "gold"}>{c.role}</Tag>
                                    </span>
                                ),
                            }))}
                        />
                    ) : (
                        <Alert
                            type="warning"
                            showIcon
                            message="Aucun représentant ni ayant droit"
                            description="Ajoutez d'abord un représentant (ou un ayant droit) depuis la fiche de la victime, puis relancez le règlement."
                        />
                    )}
                </div>
            )}

            {/* Motif du règlement — OBLIGATOIRE (Select des motifs existants + saisie libre). */}
            <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>Motif du règlement (choisir un motif existant)</div>
                <Select
                    style={{ width: "100%" }}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Sélectionner un motif existant"
                    options={motifOptions}
                    value={motifOptions.some((o) => o.value === motifText) ? motifText : undefined}
                    onChange={(v) => setMotifText(v || "")}
                />
                <div style={{ fontSize: 13, margin: "10px 0 4px" }}>
                    Motif <span style={{ color: "#dc2626" }}>*</span> (personnalisable)
                </div>
                <Input.TextArea
                    rows={3}
                    placeholder="Préciser / personnaliser le motif du règlement"
                    value={motifText}
                    onChange={(e) => setMotifText(e.target.value)}
                />
            </div>

            <div style={{ marginTop: 16, padding: "10px 12px", background: "#f6f7fb", borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Le chèque sera libellé à l'ordre de{" "}
                    <Text strong>
                        {mode === "BNCB"
                            ? (bncb?.nom || "Bureau National de la Carte Brune")
                            : (mode === "REPRESENTANT" ? (selectedName || "…") : victimeName)}
                    </Text>
                    {(mode === "REPRESENTANT" || mode === "BNCB") && <> — pour le compte de <Text strong>{victimeName}</Text></>}.
                </Text>
            </div>
        </Modal>
    );
};

ChoixBeneficiaireChequeModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    victime: PropTypes.object,
    onConfirm: PropTypes.func,
    loading: PropTypes.bool,
    title: PropTypes.string,
    okText: PropTypes.string,
    carteBrune: PropTypes.bool,
    bncb: PropTypes.object,
    motifOptions: PropTypes.array,
};

export default ChoixBeneficiaireChequeModal;
